import { useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, Check, Loader2, RefreshCw, Sparkles, X } from "lucide-react";
import {
  useAccounts,
  useClassifyStatus,
  usePushStatus,
  useSyncLog,
  useSyncStatus,
} from "../../hooks/useApi";

/** "in 5d" / "2m ago" for the push block. */
function relative(iso: string): string {
  const diff = new Date(iso).getTime() - Date.now();
  const abs = Math.abs(diff);
  const unit =
    abs >= 86_400_000
      ? `${Math.round(abs / 86_400_000)}d`
      : abs >= 3_600_000
        ? `${Math.round(abs / 3_600_000)}h`
        : `${Math.max(1, Math.round(abs / 60_000))}m`;
  return diff >= 0 ? `in ${unit}` : `${unit} ago`;
}

/** Full-screen dialog with the server's sync event log, newest first. */
function SyncLogDialog({ onClose }: { onClose: () => void }) {
  const { data: log } = useSyncLog(true);
  const { data: push } = usePushStatus(true);
  return (
    <div
      className="fixed inset-0 z-[80] grid place-items-center bg-black/30"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="flex max-h-[70vh] w-[620px] max-w-[94vw] flex-col rounded-lg border border-stroke bg-white shadow-xl">
        <div className="flex items-center justify-between border-b border-stroke px-4 py-2.5">
          <h2 className="text-[14px] font-semibold">Sync details</h2>
          <button
            onClick={onClose}
            aria-label="Close"
            className="grid size-6 place-items-center rounded text-ink-muted hover:bg-black/5 hover:text-ink"
          >
            <X size={14} />
          </button>
        </div>
        {(push ?? []).length > 0 && (
          <div className="border-b border-stroke px-4 py-2 text-[11.5px]">
            <p className="pb-1 font-semibold text-ink-muted">Push notifications</p>
            {(push ?? []).map((p) => (
              <p key={p.accountId} className="flex items-center gap-1.5">
                {p.lastError ? (
                  <AlertTriangle size={11} className="shrink-0 text-amber-600" />
                ) : (
                  <Check size={11} className="shrink-0 text-green-700" />
                )}
                <span className="min-w-0 truncate">{p.username}</span>
                <span className="shrink-0 text-ink-muted">
                  {p.kind} · renews {relative(p.expiresAt)}
                  {p.lastEventAt ? ` · last event ${relative(p.lastEventAt)}` : ""}
                </span>
                {p.lastError && (
                  <span className="min-w-0 truncate text-amber-700" title={p.lastError}>
                    {p.lastError}
                  </span>
                )}
              </p>
            ))}
          </div>
        )}
        <div className="min-h-0 flex-1 overflow-y-auto px-4 py-2 font-mono text-[11.5px] leading-5">
          {(log ?? [])
            .slice()
            .reverse()
            .map((e, i) => (
              <p key={`${e.t}-${i}`} className="flex gap-2">
                <span className="shrink-0 text-ink-muted">
                  {new Date(e.t).toLocaleTimeString()}
                </span>
                <span className={e.msg.includes("FAILED") ? "text-red-700" : ""}>{e.msg}</span>
              </p>
            ))}
          {(log ?? []).length === 0 && (
            <p className="py-4 text-center text-ink-muted">No sync events recorded yet.</p>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * Bottom-right progress card while the background mail sync runs: one line
 * per account (spinner / new-mail count / error), so the user sees exactly
 * which account is loading or unreachable instead of a bare spinner.
 */
/** "12s" / "1m 05s" from a start/end epoch-ms pair (end defaults to now). */
function elapsed(start?: number, end?: number): string | null {
  if (!start) return null;
  const s = Math.max(0, Math.round(((end ?? Date.now()) - start) / 1000));
  return s >= 60 ? `${Math.floor(s / 60)}m ${String(s % 60).padStart(2, "0")}s` : `${s}s`;
}

export function SyncProgressPanel() {
  const { data: status } = useSyncStatus();
  const { data: classify } = useClassifyStatus();
  const { data: accounts } = useAccounts();
  const queryClient = useQueryClient();

  const anyRunning = !!status?.running || !!classify?.running;

  // 1s tick so the elapsed counters move between status polls.
  const [, setTick] = useState(0);
  useEffect(() => {
    if (!anyRunning) return;
    const t = setInterval(() => setTick((n) => n + 1), 1000);
    return () => clearInterval(t);
  }, [anyRunning]);
  // Keep the panel up briefly after work finishes so results are readable.
  const [linger, setLinger] = useState(false);
  useEffect(() => {
    if (anyRunning) {
      setLinger(true);
      return;
    }
    if (!linger) return;
    const t = setTimeout(() => setLinger(false), 4000);
    return () => clearTimeout(t);
  }, [anyRunning, linger]);

  // When a classification run finishes, pull the fresh labels/counts into view.
  const wasClassifying = useRef(false);
  useEffect(() => {
    if (classify?.running) wasClassifying.current = true;
    else if (wasClassifying.current) {
      wasClassifying.current = false;
      queryClient.invalidateQueries({ queryKey: ["labels"] });
      queryClient.invalidateQueries({ queryKey: ["filter-counts"] });
      queryClient.invalidateQueries({ queryKey: ["mail-stats"] });
    }
  }, [classify?.running, queryClient]);

  // Only show the sync section for syncs (not while a classify-only run lingers).
  const [showSync, setShowSync] = useState(false);
  useEffect(() => {
    if (status?.running) setShowSync(true);
    else if (!linger) setShowSync(false);
  }, [status?.running, linger]);

  const [detailsOpen, setDetailsOpen] = useState(false);

  if ((!status && !classify) || (!anyRunning && !linger) || !accounts?.length) {
    // Keep the dialog reachable even after the panel hides itself.
    return detailsOpen ? <SyncLogDialog onClose={() => setDetailsOpen(false)} /> : null;
  }

  const rows = showSync
    ? accounts.map((a) => ({ account: a, s: status?.accounts[a.id] })).filter((r) => r.s)
    : [];

  return (
    <div className="fixed bottom-4 right-4 z-40 w-[280px] rounded-lg border border-stroke bg-white p-3 shadow-lg">
      <button
        onClick={() => setDetailsOpen(true)}
        className="absolute right-2 top-2 rounded px-1.5 py-0.5 text-[11px] font-medium text-accent hover:bg-accent-subtle"
      >
        Details
      </button>
      {detailsOpen && <SyncLogDialog onClose={() => setDetailsOpen(false)} />}
      {classify && (classify.running || (linger && classify.processed > 0)) && (
        <p className="flex items-center gap-1.5 pb-2 pr-12 text-[12px] font-semibold text-ink">
          <Sparkles
            size={13}
            className={classify.running ? "animate-pulse text-accent" : "text-green-700"}
          />
          {classify.running
            ? `Classifying "${classify.filterName}": ${classify.processed}/${classify.total} (${classify.matched} match)`
            : `"${classify.filterName}": ${classify.matched} of ${classify.processed} matched`}
        </p>
      )}
      {showSync && (
        <p className="flex items-center gap-1.5 pb-2 pr-12 text-[12px] font-semibold text-ink">
          <RefreshCw
            size={13}
            className={status?.running ? "animate-spin text-accent" : "text-ink-muted"}
          />
          <span className="flex-1">{status?.running ? "Syncing mail…" : "Sync finished"}</span>
          <span className="font-normal text-ink-muted">
            {status?.running
              ? elapsed(status.startedAt)
              : elapsed(status?.startedAt, status?.finishedAt)}
          </span>
        </p>
      )}
      <div className="flex flex-col gap-1">
        {rows.map(({ account, s }) => (
          <div key={account.id} className="flex items-center gap-2 text-[12px]">
            <span className="min-w-0 flex-1 truncate text-ink-muted">{account.username}</span>
            {s!.running ? (
              <span className="flex shrink-0 items-center gap-1 text-accent">
                <Loader2 size={12} className="animate-spin" />
                {s!.reconcile ? "deep · " : ""}
                {s!.inserted > 0 ? `+${s!.inserted} · ` : ""}
                {(s!.removed ?? 0) > 0 ? `−${s!.removed} · ` : ""}
                {s!.startedAt ? elapsed(s!.startedAt) : "queued"}
              </span>
            ) : s!.error ? (
              <span
                className="flex shrink-0 items-center gap-1 text-amber-600"
                title={s!.error}
              >
                <AlertTriangle size={12} /> unreachable
              </span>
            ) : (
              <span className="flex shrink-0 items-center gap-1 text-green-700">
                <Check size={12} />
                {[
                  s!.inserted > 0 ? `+${s!.inserted} new` : "",
                  (s!.removed ?? 0) > 0 ? `−${s!.removed} removed` : "",
                ]
                  .filter(Boolean)
                  .join(" · ") || "up to date"}
                {s!.startedAt && s!.finishedAt ? ` · ${elapsed(s!.startedAt, s!.finishedAt)}` : ""}
              </span>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
